import { Item, ItemTree } from "@/utils/explorer";
import { createContext, useContext } from "react";
import { StoreApi, useStore } from "zustand";

export type Size = 'small' | 'medium' | 'large';

export interface ViewOptions {
  size: Size;
}

export interface Directory {
  items: Record<string, Item>;
  tree: ItemTree;

  expanded: Set<string>;
}

export interface ExplorerInfo {
  directories: Record<string, Directory>;
  prefix: string;

  view: ViewOptions;
  tags: string[];
  season?: string;
}

export interface ExplorerActions {
  setOpenState(directoryId: string, id: string, isOpen: boolean): void;
  setViewSize(size: Size): void;
  setExpandedTags(tags: string[]): void;
  setExpandedSeason(season?: string): void;
}

export type ExplorerService = ExplorerInfo & ExplorerActions;

export const ExplorerContext = createContext<StoreApi<ExplorerService> | null>(null);



export function useExplorer<U>(selector: (store: ExplorerService) => U): U {
  const context = useContext(ExplorerContext);
  if (!context) {
    throw new Error('useExplorer must be used within a ExplorerProvider');
  }
  return useStore(context, selector);
}

export function useIsOpen(directoryId: string, id: string) {
  const setOpenState = useExplorer(state => state.setOpenState);
  const selected = useExplorer(state => state.directories[directoryId]?.expanded.has(id));
  return [selected, setOpenState] as const;
}

export function useItem(directoryId: string, id: string) {
  return useExplorer(state => state.directories[directoryId]?.items[id]);
}

export const searchForItem = (id: string, tree: ItemTree): ItemTree | undefined => {
  if (tree.id === id) {
    return tree;
  }

  if (tree.items) {
    for (const child of tree.items) {
      const found = searchForItem(id, child);
      if (found) return found;
    }
  }
  return;
};

export function useItemTree(directoryId: string, id: string) {
  return useExplorer(state => {
    const directory = state.directories[directoryId];
    if (!directory) return;
    return searchForItem(id, directory.tree);
  });
}

const getSize = (size: Size) => {
  switch (size) {
    case 'small':
      return {
        grid: 'grid-cols-[repeat(auto-fill,minmax(8rem,1fr))]',
        asset: 'h-[8rem]'
      };
    case 'medium':
      return {
        grid: 'grid-cols-[repeat(auto-fill,minmax(12rem,1fr))]',
        asset: 'h-[12rem]'
      };
    case 'large':
      return {
        grid: 'grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]',
        asset: 'h-[18rem]'
      };
  }
};

export function useSize() {
  const [size, setSize] = useExplorer(state => [state.view.size, state.setViewSize] as const);
  return [getSize(size), setSize] as const;
}

export const EXPANDED_CACHE_KEY = `sc:miz:explorer:selected`;
export const restoreExpanded = (directoryId: string) => {
  const expanded = localStorage.getItem(`${EXPANDED_CACHE_KEY}:${directoryId}`);
  try {
    if (!expanded) throw new Error('No selected items');
    const parsed = JSON.parse(expanded);
    return new Set<string>(parsed);
  } catch {
    return new Set<string>();
  }
};

export const backupExpanded = (directoryId: string, expanded: Set<string>) => {
  localStorage.setItem(`${EXPANDED_CACHE_KEY}:${directoryId}`, JSON.stringify([...expanded]));
};

export const SIZE_CACHE_KEY = `sc:miz:explorer:size`;
export const restoreSize = () => {
  const size = localStorage.getItem(`${SIZE_CACHE_KEY}`);
  try {
    if (!size) throw new Error('No size');
    return size as Size;
  } catch {
    return 'medium';
  }
};

export const backupSize = (size: Size) => {
  localStorage.setItem(`${SIZE_CACHE_KEY}`, size);
};
